import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, Link, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
    LayoutDashboard, Flag, DollarSign, Link2, Zap, BarChart3,
    FileText, Search, Users, UserCircle, Settings, HelpCircle,
    Bell, Bot, Menu, X, Moon, Sun, LogOut, ExternalLink, Layout, Megaphone, Globe, Package, Send, Radar, RotateCcw, Clock,
    CalendarDays, ShieldCheck, Building2, CreditCard, History, ClipboardList, SunMedium, Star, Lock, Trophy, MessageSquare, ArrowUpRight, Shield, Type, Kanban, ArrowLeft
} from 'lucide-react';
import { SystemTour, useTourReset } from '../Tour';
import { TabTour } from '../TabTour';
import { PlatformIcon } from '../PlatformIcon';
import { PageHelp } from '../PageHelp';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { API_BASE } from '../../config/apiBase';
import UpgradeModal from '../UpgradeModal';
import AnimatedBrandIcon from '../AnimatedBrandIcon';
import MarcaTexto from '../brand/MarcaTexto';
import BiometricPrompt from '../BiometricPrompt';
import CategoryOnboardingModal from '../CategoryOnboardingModal';
import { OverlayQueue, OVERLAY_PRIORITY } from '../../utils/overlayQueue';
import { getNavSections, getTabKeyForPath } from '../../config/navRegistry';
import { resolveImageUrl } from '../../utils/resolveImageUrl';

// ============ NAV SECTIONS FROM REGISTRY ============
// Nav sections are now derived from navRegistry.js (single source of truth for nav + tours)
// Icons are imported from lucide-react for bottom nav only (not registry-managed)
const ADMIN_BOTTOM = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/sistema' },
    { key: 'campanhas', label: 'Campanhas', icon: Flag, path: '/sistema/campanhas' },
    { key: 'alertas', label: 'Alertas', icon: Bell, path: '/sistema/alertas' },
    { key: 'conta', label: 'Perfil', icon: UserCircle, path: '/sistema/conta' },
];

// ============ EMPRESA NAV (from registry) ============
const EMPRESA_BOTTOM = [
    { key: 'emp-dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/sistema/empresa' },
    { key: 'ponto', label: 'Ponto', icon: Clock, path: '/sistema/ponto' },
    { key: 'emp-colaboradores', label: 'Equipe', icon: Users, path: '/sistema/empresa/colaboradores' },
    { key: 'conta', label: 'Perfil', icon: UserCircle, path: '/sistema/conta' },
];

// ============ COLABORADOR NAV (from registry) ============
const COLABORADOR_BOTTOM = [
    { key: 'col-dashboard', label: 'Inicio', icon: LayoutDashboard, path: '/sistema/colaborador' },
    { key: 'col-meudia', label: 'Meu Dia', icon: SunMedium, path: '/sistema/colaborador/meu-dia' },
    { key: 'ponto', label: 'Ponto', icon: Clock, path: '/sistema/ponto' },
    { key: 'conta', label: 'Perfil', icon: UserCircle, path: '/sistema/conta' },
];

const PAGE_META = {
    '/sistema': { title: 'Dashboard', subtitle: 'Visão geral do sistema', helpKey: 'dashboard' },
    '/sistema/campanhas': { title: 'Campanhas', subtitle: 'Gerenciamento de campanhas', helpKey: 'campanhas' },
    '/sistema/gestao': { title: 'Gestão', subtitle: 'Financeiro e operacional', helpKey: 'gestao' },
    '/sistema/pedidos': { title: 'Pedidos de Oferta', subtitle: 'Demandas e categorias inteligentes' },
    '/sistema/conexoes': { title: 'Conexões', subtitle: 'Canais e integrações', helpKey: 'conexoes' },
    '/sistema/automacoes': { title: 'Automações', subtitle: 'Regras e agendamentos', helpKey: 'automacoes' },
    '/sistema/performance': { title: 'Performance', subtitle: 'Métricas e relatórios', helpKey: 'performance' },
    '/sistema/templates': { title: 'Templates', subtitle: 'Modelos de mensagem', helpKey: 'templates' },
    '/sistema/pesquisas': { title: 'Pesquisas Focadas', subtitle: 'Catálogo de pesquisa', helpKey: 'pesquisas' },
    '/sistema/comunidades': { title: 'Publicar em Comunidades', subtitle: 'Promobit, Pelando e mais', helpKey: 'comunidades' },
    '/sistema/comissoes': { title: 'Comissões', subtitle: 'Comissão real dos programas de afiliado' },
    '/sistema/descobertas': { title: 'Descobertas', subtitle: 'Ofertas rastreadas automaticamente', helpKey: 'descobertas' },
    '/sistema/cowork-ofertas': { title: 'Cowork Ofertas', subtitle: 'Candidatos do Cowork em shadow: nada é publicado' },
    '/sistema/ml-afiliados': { title: 'Mercado Livre — Cobertura de Afiliados', subtitle: 'Workflow manual oficial: lote, Portal, preview e import' },
    '/sistema/amazon-manual': { title: 'Amazon — Oferta manual (SiteStripe)', subtitle: 'Link oficial intacto, sem preço, publicação só com preview' },
    '/sistema/usuarios': { title: 'Usuários', subtitle: 'Diretório e permissões', helpKey: 'usuarios' },
    '/sistema/alertas': { title: 'Alertas', subtitle: 'Notificações e comunicados' },
    '/sistema/notificacoes': { title: 'Notificações Push', subtitle: 'Alertas de ofertas de alta qualidade' },
    '/sistema/copilot': { title: 'Copilot IA', subtitle: 'Assistente inteligente' },
    '/sistema/conta': { title: 'Minha Conta', subtitle: 'Perfil e preferências', helpKey: 'conta' },
    '/sistema/configuracoes': { title: 'Configurações', subtitle: 'Preferências do sistema', helpKey: 'configuracoes' },
    '/sistema/login-social': { title: 'Login Social', subtitle: 'Configurar provedores de autenticação' },
    '/sistema/anuncios': { title: 'Anúncios', subtitle: 'Gerenciar monetização do site' },
    '/sistema/ponto': { title: 'Ponto Digital', subtitle: 'Controle de ponto' },
    '/sistema/funcionarios': { title: 'Colaboradores', subtitle: 'Gerencie sua equipe' },
    '/sistema/suporte': { title: 'Suporte', subtitle: 'Ajuda e documentação', helpKey: 'suporte' },
    '/sistema/admin/empresas': { title: 'Empresas & Planos', subtitle: 'Gerenciar contas empresariais' },
    '/sistema/admin/crm': { title: 'CRM HUB', subtitle: 'Gestão comercial e relacionamento empresarial' },
    '/sistema/admin/indicacoes': { title: 'Indicações', subtitle: 'Gerenciar profissionais recomendados' },
    '/sistema/admin/analytics': { title: 'Analytics', subtitle: 'Métricas e indicadores da plataforma' },
    '/sistema/admin/master': { title: 'Admin Master', subtitle: 'Gestao de integracoes, funil e inteligencia' },
    '/sistema/marketplaces': { title: 'Marketplaces', subtitle: 'Lojas e plataformas integradas' },
    '/sistema/empresa/indicacoes': { title: 'Indicações', subtitle: 'Profissionais recomendados para sua empresa' },
    // Empresa pages
    '/sistema/empresa': { title: 'Dashboard', subtitle: 'Visão geral da empresa' },
    '/sistema/empresa/gestao': { title: 'Gestão', subtitle: 'Operação da empresa' },
    '/sistema/empresa/performance': { title: 'Performance', subtitle: 'Indicadores e métricas' },
    '/sistema/empresa/comparativo': { title: 'Comparativo', subtitle: 'Métricas por unidade' },
    '/sistema/empresa/colaboradores': { title: 'Colaboradores', subtitle: 'Gerencie sua equipe' },
    '/sistema/empresa/unidades': { title: 'Unidades / Filiais', subtitle: 'Gerencie suas unidades' },
    '/sistema/empresa/escalas': { title: 'Escalas e Jornadas', subtitle: 'Horarios e turnos' },
    '/sistema/empresa/permissoes': { title: 'Permissões e Acessos', subtitle: 'Controle de acesso' },
    '/sistema/empresa/configuracoes': { title: 'Configurações', subtitle: 'Dados da empresa' },
    '/sistema/empresa/assinatura': { title: 'Assinatura / Plano', subtitle: 'Seu plano atual' },
    '/sistema/empresa/solicitacoes': { title: 'Solicitações / Ajustes', subtitle: 'Pedidos e correções' },
    '/sistema/empresa/financeiro': { title: 'Financeiro', subtitle: 'Receitas, despesas e controle financeiro' },
    '/sistema/empresa/open-finance': { title: 'Open Finance', subtitle: 'Conexoes bancarias, extratos e gestao financeira' },
    // Colaborador pages
    '/sistema/colaborador': { title: 'Dashboard', subtitle: 'Seu painel' },
    '/sistema/colaborador/meu-dia': { title: 'Meu Dia', subtitle: 'Resumo do seu turno' },
    '/sistema/colaborador/historico': { title: 'Meu Histórico', subtitle: 'Marcações e registros' },
    '/sistema/colaborador/solicitacoes': { title: 'Solicitações', subtitle: 'Ajustes e pedidos' },
};

// PATH_TO_TAB_KEY now comes from navRegistry.js via getTabKeyForPath()

const SUBNAV_CONFIG = {
    '/sistema': [
        { label: 'Resumo', kind: 'anchor', param: 'resumo' },
        { label: 'Campanhas', kind: 'route', path: '/sistema/campanhas' },
        { label: 'Conexões', kind: 'route', path: '/sistema/conexoes' },
        { label: 'Automações', kind: 'route', path: '/sistema/automacoes' },
        { label: 'Alertas', kind: 'route', path: '/sistema/alertas' },
    ],
    '/sistema/campanhas': [
        { label: 'Hub', kind: 'anchor', param: 'hub' },
        { label: 'Nova campanha', kind: 'action', param: 'nova_campanha' },
        { label: 'Templates', kind: 'route', path: '/sistema/templates' },
        { label: 'Pesquisas', kind: 'route', path: '/sistema/pesquisas' },
    ],
    '/sistema/conexoes': [
        { label: 'Canais', kind: 'anchor', param: 'canais' },
        { label: 'Novo canal', kind: 'action', param: 'novo_canal' },
    ],
    '/sistema/automacoes': [
        { label: 'Visão', kind: 'anchor', param: 'visao' },
        { label: 'Nova automação', kind: 'action', param: 'nova_automacao' },
    ],
    '/sistema/gestao': [
        { label: 'Resumo', kind: 'tab', param: 'resumo' },
        { label: 'Financeiro', kind: 'tab', param: 'financeiro' },
        { label: 'Contas', kind: 'tab', param: 'contas' },
        { label: 'Auditoria', kind: 'tab', param: 'auditoria' },
    ],
    '/sistema/pedidos': [
        { label: 'Pedidos', kind: 'tab', param: 'pedidos' },
        { label: 'Categorias', kind: 'tab', param: 'categorias' },
        { label: 'Novo Pedido', kind: 'action', param: 'novo_pedido' },
    ],
    '/sistema/performance': [
        { label: 'Visao', kind: 'anchor', param: 'visao' },
        { label: 'Métricas', kind: 'anchor', param: 'metricas' },
        { label: 'Campanhas', kind: 'route', path: '/sistema/campanhas' },
        { label: 'Gestão', kind: 'route', path: '/sistema/gestao' },
    ],
    '/sistema/usuarios': [
        { label: 'Diretorio', kind: 'anchor', param: 'diretorio' },
        { label: 'Novo usuario', kind: 'action', param: 'novo_usuario' },
    ],
    '/sistema/templates': [
        { label: 'Modelos', kind: 'anchor', param: 'modelos' },
        { label: 'Novo template', kind: 'action', param: 'novo_template' },
    ],
    '/sistema/comunidades': [
        { label: 'Publicacoes', kind: 'tab', param: 'publicacoes' },
        { label: 'Plataformas', kind: 'tab', param: 'plataformas' },
        { label: 'Histórico', kind: 'tab', param: 'historico' },
    ],
};

export default function SystemLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeComunicado, setActiveComunicado] = useState(null);
    const [showGroups, setShowGroups] = useState(false);
    const [groupLinks, setGroupLinks] = useState([]);
    const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: '', plan: 'start' });
    const [animationsEnabled, setAnimationsEnabled] = useState(() => localStorage.getItem('sv_animations') !== 'off');
    const [compactSidebar, setCompactSidebar] = useState(() => localStorage.getItem('sv_compact_sidebar') === 'on');
    const TEXT_SIZES = ['default', 'medium', 'large'];
    const TEXT_SIZE_LABELS = { default: 'A', medium: 'A+', large: 'A++' };
    const TEXT_SIZE_TITLES = { default: 'Texto: Padrão', medium: 'Texto: Médio', large: 'Texto: Grande' };
    const [textSize, setTextSize] = useState(() => localStorage.getItem('sv_text_size') || 'default');
    const { user, loading, logout } = useAuth();
    const { toggleTheme, isDark, layoutMode, toggleLayoutMode } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isMenuBlocked, getUpgradeInfo, plan, MENU_MODULE_MAP } = usePlanLimits();
    const resetTour = useTourReset('system');

    const [systemSearchOpen, setSystemSearchOpen] = useState(false);
    const [systemSearchQuery, setSystemSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const systemSearchRef = useRef(null);
    const searchTimerRef = useRef(null);
    const [showCategoryOnboarding, setShowCategoryOnboarding] = useState(false);

    // Check if category onboarding is needed
    useEffect(() => {
        if (!user) return;
        const done = localStorage.getItem('sv_cat_onboarding_done');
        if (done) return;
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        let cancelled = false;
        fetch(`${API_BASE}/api/empresa/category-preferences`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : null)
            .then(async data => {
                if (cancelled) return;
                if (data && !data.onboarding_done) {
                    // Route through the overlay queue — category has top priority.
                    // This ensures it opens FIRST and nothing else opens while it's visible.
                    await OverlayQueue.acquire('category_onboarding', OVERLAY_PRIORITY.CATEGORY);
                    if (cancelled) { OverlayQueue.release('category_onboarding'); return; }
                    setShowCategoryOnboarding(true);
                } else if (data?.onboarding_done) {
                    localStorage.setItem('sv_cat_onboarding_done', '1');
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user]);

    // Contextual search logic
    const getSearchContext = () => {
        const path = location.pathname.replace('/sistema/', '').replace('/sistema', '');
        const map = { campanhas: 'campanhas', gestao: 'gestao', 'admin/crm': 'admin-crm', pedidos: 'pedidos', descobertas: 'descobertas', conexoes: 'conexoes', marketplaces: 'marketplaces', usuarios: 'usuarios', alertas: 'alertas', comunidades: 'comunidades', templates: 'templates' };
        return map[path] || '';
    };

    const doSearch = async (q) => {
        if (!q || q.length < 2) { setSearchResults(null); return; }
        setSearchLoading(true);
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const ctx = getSearchContext();
            const res = await fetch(`${API_BASE}/api/system/search?q=${encodeURIComponent(q)}&context=${ctx}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setSearchResults(await res.json());
        } catch { }
        setSearchLoading(false);
    };

    const handleSearchInput = (val) => {
        setSystemSearchQuery(val);
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => doSearch(val), 300);
    };

    const acType = user?.account_type || 'platform';
    const isAdmin = user?.role === 'admin';
    const isEmpresa = acType === 'empresa' || acType === 'client';
    const isColaborador = acType === 'colaborador' || acType === 'client_user';

    const toggleAnimations = () => {
        const next = !animationsEnabled;
        setAnimationsEnabled(next);
        localStorage.setItem('sv_animations', next ? 'on' : 'off');
        document.documentElement.classList.toggle('sv-no-animations', !next);
    };

    const cycleTextSize = () => {
        const idx = TEXT_SIZES.indexOf(textSize);
        const next = TEXT_SIZES[(idx + 1) % TEXT_SIZES.length];
        setTextSize(next);
        localStorage.setItem('sv_text_size', next);
    };

    const toggleCompactSidebar = () => {
        const next = !compactSidebar;
        setCompactSidebar(next);
        localStorage.setItem('sv_compact_sidebar', next ? 'on' : 'off');
    };

    // Apply animations class on mount
    useEffect(() => {
        if (!animationsEnabled) document.documentElement.classList.add('sv-no-animations');
        const onSettings = () => {
            setAnimationsEnabled(localStorage.getItem('sv_animations') !== 'off');
            setCompactSidebar(localStorage.getItem('sv_compact_sidebar') === 'on');
            document.documentElement.classList.toggle('sv-no-animations', localStorage.getItem('sv_animations') === 'off');
        };
        window.addEventListener('sv-settings-changed', onSettings);
        return () => { document.documentElement.classList.remove('sv-no-animations'); window.removeEventListener('sv-settings-changed', onSettings); };
    }, []);

    // Redirect to correct home based on profile
    useEffect(() => {
        if (!user) return;
        if (location.pathname === '/sistema') {
            if (isEmpresa) navigate('/sistema/empresa', { replace: true });
            else if (isColaborador) navigate('/sistema/colaborador', { replace: true });
        }
    }, [location.pathname, isEmpresa, isColaborador, navigate, user]);

    // Fetch active comunicados on mount
    useEffect(() => {
        if (!user) return;
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        fetch(`${API_BASE}/api/comunicados/active`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(data => { if (Array.isArray(data) && data.length > 0) setActiveComunicado(data[0]); })
            .catch(() => {});
        fetch(`${API_BASE}/api/platform-links/active`)
            .then(r => r.json())
            .then(data => { if (Array.isArray(data)) setGroupLinks(data); })
            .catch(() => {});
    }, [user]);

    // Auto-close popup after duration
    useEffect(() => {
        if (activeComunicado?.duration_seconds > 0) {
            const timer = setTimeout(() => setActiveComunicado(null), activeComunicado.duration_seconds * 1000);
            return () => clearTimeout(timer);
        }
    }, [activeComunicado]);

    // AUTH GUARD: Wait for auth hydration before rendering anything
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }} data-testid="system-auth-loading">
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: 36, height: 36, border: '3px solid var(--border)', borderTopColor: 'var(--sv-gold-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                    <div style={{ color: 'var(--muted-2)', fontSize: 13 }}>Carregando...</div>
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // AUTH GUARD: If auth resolved and no user, redirect to login
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Select nav/bottom based on profile
    const navSections = isEmpresa ? getNavSections('empresa') : isColaborador ? getNavSections('colaborador') : getNavSections('admin');
    const bottomNav = isEmpresa ? EMPRESA_BOTTOM : isColaborador ? COLABORADOR_BOTTOM : ADMIN_BOTTOM;

    const currentPath = location.pathname;
    const meta = PAGE_META[currentPath] || { title: 'Sistema', subtitle: 'SuperVitrine' };
    const subnav = (isEmpresa || isColaborador) ? [] : (SUBNAV_CONFIG[currentPath] || []);
    const currentTabKey = getTabKeyForPath(currentPath) || '';

    // Handle menu click with plan check (only for empresa)
    const handleNavClick = (item, e) => {
        if (isEmpresa && isMenuBlocked(item.key)) {
            e.preventDefault();
            const moduleKey = MENU_MODULE_MAP[item.key];
            const info = getUpgradeInfo(moduleKey);
            setUpgradeModal({ open: true, feature: item.label, plan: info.plan });
            return false;
        }
        setSidebarOpen(false);
        return true;
    };

    const dismissComunicado = () => {
        if (activeComunicado) {
            const token = localStorage.getItem('token');
            fetch(`${API_BASE}/api/comunicados/${activeComunicado.comunicado_id}/dismiss`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }).catch(() => {});
        }
        setActiveComunicado(null);
    };

    const isActive = (path) => {
        if (path === '/sistema') return currentPath === '/sistema';
        if (path === '/sistema/empresa') return currentPath === '/sistema/empresa';
        if (path === '/sistema/colaborador') return currentPath === '/sistema/colaborador';
        return currentPath === path || (currentPath.startsWith(path + '/'));
    };

    const handleLogout = async () => {
        await logout();
        navigate('/', { replace: true });
    };

    return (
        <div className={`sv-system-layout ${compactSidebar ? 'sv-compact' : ''} sv-text-${textSize}`} data-testid="system-layout">
            <SystemTour />
            {currentTabKey && <TabTour tabKey={currentTabKey} key={currentTabKey} />}
            {/* Mobile Backdrop */}
            <div
                className={`sv-sidebar-backdrop ${sidebarOpen ? 'show' : ''}`}
                onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar */}
            <aside className={`sv-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="system-sidebar">
                <div className="sv-sidebar-brand">
                    <AnimatedBrandIcon size={34} animate={true} glow={false} />
                    <div className="sv-sidebar-brand-text">
                        <MarcaTexto className="sv-sidebar-brand-title" />
                        <span className="sv-sidebar-brand-sub">Painel</span>
                    </div>
                </div>

                <nav className="sv-sidebar-nav">
                    {navSections.map((section) => (
                        <div key={section.label}>
                            <div className="sv-nav-section-label">{section.label}</div>
                            {section.items.map((item) => {
                                if (item.external) {
                                    return (
                                        <button
                                            key={item.key}
                                            className="sv-nav-item w-full"
                                            onClick={() => window.open(item.external, '_blank', 'noopener,noreferrer')}
                                            data-testid={`nav-${item.key}`}
                                        >
                                            <item.icon />
                                            <span>{item.label}</span>
                                        </button>
                                    );
                                }
                                const blocked = isEmpresa && isMenuBlocked(item.key);
                                return (
                                    <Link
                                        key={item.key}
                                        to={blocked ? '#' : item.path}
                                        className={`sv-nav-item ${isActive(item.path) ? 'active' : ''} ${blocked ? 'sv-nav-blocked' : ''}`}
                                        onClick={(e) => handleNavClick(item, e)}
                                        data-testid={`nav-${item.key}`}
                                        style={blocked ? { opacity: 0.55 } : {}}
                                    >
                                        <item.icon />
                                        <span>{item.label}</span>
                                        {blocked && <Lock size={11} style={{ marginLeft: 'auto', color: 'var(--muted)', opacity: 0.6 }} />}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}

                    <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                        {/* Groups button */}
                        {groupLinks.length > 0 && (
                            <button
                                className="sv-nav-item w-full"
                                onClick={() => { setShowGroups(true); setSidebarOpen(false); }}
                                style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', borderLeft: '3px solid var(--sv-gold-primary)', marginBottom: 6 }}
                                data-testid="groups-button"
                            >
                                <Globe />
                                <span>Grupos de Ofertas</span>
                            </button>
                        )}
                    </div>
                </nav>

                <div className="sv-sidebar-footer">
                    <div className="sv-sidebar-status">
                        <div className="sv-status-chip">
                            <span className="sv-status-dot" />
                            <span>API</span>
                        </div>
                        <div className="sv-status-chip">
                            <span className="sv-status-dot" />
                            <span>Rede</span>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="sv-main">
                {/* System Header */}
                <header className="sv-sys-header" data-testid="system-header">
                    <div className="sv-sys-header-left">
                        <button
                            className="sv-hamburger-btn"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            data-testid="system-hamburger"
                        >
                            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
                        </button>
                        <div className="sv-sys-header-titles">
                            <div className="sv-sys-header-title flex items-center gap-2">{meta.title} {meta.helpKey && <PageHelp pageKey={meta.helpKey} />}</div>
                            <div className="sv-sys-header-subtitle hidden sm:block">{meta.subtitle}</div>
                        </div>
                    </div>
                    {/* System-wide contextual search */}
                    {systemSearchOpen && (
                        <div className="system-search-overlay" data-testid="system-search-overlay" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button onClick={() => { setSystemSearchOpen(false); setSystemSearchQuery(''); setSearchResults(null); }} className="system-search-back" data-testid="system-search-back">
                                    <ArrowLeft size={20} style={{ color: 'var(--text)' }} />
                                </button>
                                <form onSubmit={(e) => { e.preventDefault(); if (systemSearchQuery.trim()) { doSearch(systemSearchQuery.trim()); } }} className="system-search-form" style={{ flex: 1 }}>
                                    <input
                                        ref={systemSearchRef}
                                        type="text"
                                        placeholder={`Buscar${getSearchContext() ? ` em ${getSearchContext()}...` : ' no sistema...'}`}
                                        value={systemSearchQuery}
                                        onChange={(e) => handleSearchInput(e.target.value)}
                                        data-testid="system-search-input"
                                        autoFocus
                                    />
                                </form>
                            </div>
                            {/* Inline Results */}
                            {(searchResults || searchLoading) && (
                                <div style={{ maxHeight: '320px', overflowY: 'auto', marginTop: '8px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)', padding: '8px' }}>
                                    {searchLoading && <div className="text-xs py-2 text-center" style={{ color: 'var(--muted)' }}>Buscando...</div>}
                                    {searchResults && searchResults.context_results?.length > 0 && (
                                        <div className="mb-2">
                                            <div className="text-[9px] font-semibold px-2 py-1" style={{ color: 'var(--sv-gold-primary)' }}>NESTA PAGINA</div>
                                            {searchResults.context_results.map((r, i) => (
                                                <div key={`ctx-${i}`} className="px-2 py-1.5 rounded-lg text-xs cursor-pointer hover:opacity-80" style={{ color: 'var(--text)', background: 'rgba(var(--sv-accent-rgb), 0.06)' }}
                                                    onClick={() => { setSystemSearchOpen(false); setSearchResults(null); setSystemSearchQuery(''); }}>
                                                    <span className="text-[9px] mr-1.5 px-1 py-0.5 rounded" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>{r.label}</span>
                                                    {r.data.title || r.data.name || r.data.email || r.data.provider_id || JSON.stringify(r.data).slice(0, 60)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults && searchResults.results?.length > 0 && (
                                        <div>
                                            <div className="text-[9px] font-semibold px-2 py-1" style={{ color: 'var(--muted)' }}>OUTROS RESULTADOS</div>
                                            {searchResults.results.map((r, i) => (
                                                <div key={`res-${i}`} className="px-2 py-1.5 rounded-lg text-xs cursor-pointer hover:opacity-80" style={{ color: 'var(--text)' }}
                                                    onClick={() => { setSystemSearchOpen(false); setSearchResults(null); setSystemSearchQuery(''); }}>
                                                    <span className="text-[9px] mr-1.5 px-1 py-0.5 rounded" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>{r.label}</span>
                                                    {r.data.title || r.data.name || r.data.email || r.data.provider_id || JSON.stringify(r.data).slice(0, 60)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {searchResults && !searchLoading && searchResults.context_results?.length === 0 && searchResults.results?.length === 0 && (
                                        <div className="text-xs py-3 text-center" style={{ color: 'var(--muted)' }}>Nenhum resultado para "{systemSearchQuery}"</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <button
                        className="sv-btn-system"
                        onClick={() => { setSystemSearchOpen(true); setTimeout(() => systemSearchRef.current?.focus(), 100); }}
                        data-testid="system-search-trigger"
                        title="Buscar"
                    >
                        <Search size={15} />
                    </button>
                    <div className="sv-sys-header-right">
                        <Link to="/" className="sv-btn-system" data-testid="go-to-site">
                            <ExternalLink size={14} />
                            <span className="hidden sm:inline">Ver Site</span>
                        </Link>
                        {/* Hide extra buttons on mobile - they go to bottom nav */}
                        {/* Hidden on mobile via CSS */}
                        <button
                            className="sv-btn-system sv-hide-mobile"
                            onClick={toggleLayoutMode}
                            title={layoutMode === 'classic' ? 'Mudar para Minimalista' : 'Mudar para Clássico'}
                        >
                            <Layout size={14} />
                            <span className="hidden sm:inline">{layoutMode === 'classic' ? 'Clássico' : 'Minimal'}</span>
                        </button>
                        <button className="sv-btn-system sv-hide-mobile" onClick={toggleTheme}>
                            {isDark ? <Sun size={14} /> : <Moon size={14} />}
                        </button>
                        <button
                            className="sv-btn-system"
                            onClick={cycleTextSize}
                            title={TEXT_SIZE_TITLES[textSize]}
                            data-testid="text-size-toggle"
                            style={textSize !== 'default' ? { background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' } : {}}
                        >
                            <Type size={14} />
                            <span className="hidden sm:inline" style={{ fontSize: 11, fontWeight: 700 }}>{TEXT_SIZE_LABELS[textSize]}</span>
                        </button>
                        <button className="sv-btn-system sv-hide-mobile" onClick={resetTour} title="Refazer tour guiado" data-testid="reset-tour-btn">
                            <RotateCcw size={14} />
                        </button>
                        {user && (
                            <div className="hidden sm:flex items-center gap-2">
                                <div
                                    className="w-8 h-8 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                    style={{ border: '1px solid var(--border)' }}
                                    onClick={() => navigate('/sistema/conta')}
                                    title="Meu Perfil"
                                    data-testid="header-avatar-link"
                                >
                                    {user.picture ? (
                                        <img src={resolveImageUrl(user.picture)} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div
                                            className="w-full h-full flex items-center justify-center text-xs font-bold"
                                            style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)' }}
                                        >
                                            {user.name?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <button className="sv-btn-system" onClick={handleLogout} data-testid="system-logout">
                                    <LogOut size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                {/* Subnav */}
                {subnav.length > 0 && (
                    <div className="sv-subnav" data-testid="system-subnav">
                        <span className="sv-subnav-label">{meta.title}</span>
                        {subnav.map((item, i) => {
                            if (item.kind === 'route') {
                                return (
                                    <Link key={i} to={item.path} className="sv-subnav-chip">
                                        {item.label}
                                    </Link>
                                );
                            }
                            const isActiveTab = item.kind === 'tab' && searchParams.get('tab') === item.param;
                            return (
                                <button key={i}
                                    className={`sv-subnav-chip ${isActiveTab ? 'active' : ''}`}
                                    onClick={() => {
                                        if (item.kind === 'tab') {
                                            setSearchParams({ tab: item.param });
                                        } else if (item.kind === 'action') {
                                            setSearchParams({ action: item.param });
                                        } else {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }
                                    }}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Page Content */}
                <div className="sv-main-view">
                    <Outlet />
                </div>
            </main>

            {/* Groups Popup */}
            {showGroups && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => setShowGroups(false)} data-testid="groups-popup">
                    <div className="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)', boxShadow: '0 0 40px rgba(var(--sv-accent-rgb), 0.15)' }} onClick={e => e.stopPropagation()}>
                        <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-2">
                                <Globe size={18} style={{ color: 'var(--sv-gold-primary)' }} />
                                <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Grupos de Ofertas</h3>
                            </div>
                            <button onClick={() => setShowGroups(false)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}><X size={14} style={{ color: 'var(--muted)' }} /></button>
                        </div>
                        <div className="p-4 flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
                            {groupLinks.map(l => {
                                const colors = { telegram: '#26A5E4', whatsapp: '#25D366', instagram: '#E4405F', discord: '#5865F2', youtube: '#FF0000', site: '#3D8BFF' };
                                const c = colors[l.platform_type] || '#888';
                                return (
                                    <a key={l.link_id} href={l.url} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-xl transition-all"
                                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
                                        data-testid={`group-link-${l.link_id}`}>
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c}18` }}>
                                            <PlatformIcon name={l.platform_type} size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{l.name}</p>
                                            <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{l.platform_type?.charAt(0).toUpperCase() + l.platform_type?.slice(1)}</p>
                                        </div>
                                        <span className="px-3 py-1.5 rounded-lg text-xs font-medium shrink-0 flex items-center gap-1"
                                            style={{ background: `${c}18`, color: c, border: `1px solid ${c}30` }}>
                                            <ExternalLink size={10} /> {l.button_text || 'Entrar'}
                                        </span>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Comunicado Popup */}
            {activeComunicado && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} data-testid="comunicado-popup">
                    <div className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)', boxShadow: '0 0 40px rgba(var(--sv-accent-rgb), 0.15)' }}>
                        {activeComunicado.image_url && (
                            <img src={activeComunicado.image_url} alt="" className="w-full max-h-48 object-cover" onError={e => e.target.style.display = 'none'} />
                        )}
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <Megaphone size={18} style={{ color: 'var(--sv-gold-primary)' }} />
                                <h3 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{activeComunicado.title}</h3>
                            </div>
                            <p className="text-sm whitespace-pre-line" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>{activeComunicado.message}</p>
                        </div>
                        <div className="px-6 pb-5">
                            <button className="sv-btn-gold-system sv-btn-system w-full justify-center" onClick={dismissComunicado} data-testid="comunicado-dismiss-btn">
                                Entendi
                            </button>
                        </div>
                        {activeComunicado.duration_seconds > 0 && (
                            <div className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.5)', color: 'var(--muted-2)' }}>
                                Auto-fecha em {activeComunicado.duration_seconds}s
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Mobile Bottom Nav */}
            <nav className="sv-mobile-bottom-nav" data-testid="system-bottom-nav">
                {bottomNav.map((item) => (
                    <Link
                        key={item.key}
                        to={item.path}
                        className={`sv-bottom-nav-item ${isActive(item.path) ? 'active' : ''}`}
                        data-testid={`bottom-nav-${item.key}`}
                    >
                        {item.key === 'conta' || item.key === 'perfil' ? (
                            user?.picture ? (
                                <img src={resolveImageUrl(user.picture)} alt="" className="w-5 h-5 rounded-full object-cover" />
                            ) : (
                                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold" style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)' }}>
                                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                            )
                        ) : (
                            <item.icon size={20} />
                        )}
                        <span>{item.label}</span>
                    </Link>
                ))}
                <button onClick={handleLogout} className="sv-bottom-nav-item" data-testid="bottom-nav-logout">
                    <LogOut size={20} /><span>Sair</span>
                </button>
            </nav>

            {/* Upgrade Modal */}
            <UpgradeModal
                isOpen={upgradeModal.open}
                onClose={() => setUpgradeModal({ ...upgradeModal, open: false })}
                feature={upgradeModal.feature}
                upgradePlan={upgradeModal.plan}
                currentPlan={plan}
            />

            {/* Category Onboarding - queued: always opens FIRST (priority 1) */}
            {showCategoryOnboarding && (
                <CategoryOnboardingModal onComplete={() => {
                    setShowCategoryOnboarding(false);
                    localStorage.setItem('sv_cat_onboarding_done', '1');
                    OverlayQueue.release('category_onboarding');
                }} />
            )}

            {/* Biometric Prompt - mobile only, after first login */}
            <BiometricPrompt />
        </div>
    );
}
